import React, { useMemo, useCallback } from 'react'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useNavigate } from 'react-router-dom'
import { Box } from '@mui/material'
import { PageWrapper } from '../../../shared/components'
import { HomeIcon } from '@heroicons/react/24/outline'
import { PageHeader } from '../../../shared/components/PageHeader'
import {
  SprintGamerProgress,
  DontForgetTaskComponent,
  TryThisAtWorkComponent,
  TryThisNextTimeComponent
} from '../components'
import type { DontForgetTaskConfigItem } from '../components/config/dontForgetConfig'
import {
  useGetUpcomingActivitiesListForGamer,
  useLatestFeedbackDataForGamer
} from '../../../apiLayer/queryHooks/queries'
import { useUser } from '../../../hooks/useUser'
import { hasUnreadLatestFeedback } from './helpers/latestSessionFeedbackHelpers'
import { useFeatureFlags } from '../../../shared/hooks/useFeatureFlags'
import { ActivityWithSessions } from '../../../types/GeneralTypes'
import { GamerRoutes } from '../../../config/routes/routeEnums'

const EMPTY_ACTIVITIES: ActivityWithSessions[] = []

interface DashboardGamerProps {
  userName?: string
  className?: string
}

/**
 * DashboardGamerPage component - Main dashboard view for gamer role
 * Displays welcome message, recommendations, sprint progress, and upcoming activities
 */
export const DashboardGamerPage: React.FC<DashboardGamerProps> = ({ userName = 'John', className = '' }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isLatestSessionFeedbackEnabled } = useFeatureFlags()

  // Fetch user data for teamIds
  const { data: userData } = useUser()

  // Fetch latest session feedback meta for "Don't forget" gating
  const { data: latestFeedbackResponse } = useLatestFeedbackDataForGamer({
    enabled: Boolean(isLatestSessionFeedbackEnabled)
  })

  const latestFeedbackData = latestFeedbackResponse?.data
  const showFeedbackTask =
    Boolean(isLatestSessionFeedbackEnabled) && hasUnreadLatestFeedback(latestFeedbackData, userData)

  // Fetch upcoming activities list for sprint derivation
  const { data: activitiesResponse } = useGetUpcomingActivitiesListForGamer()
  const upcomingActivities: ActivityWithSessions[] = activitiesResponse?.data ?? EMPTY_ACTIVITIES

  const handleDontForgetNavigation = useCallback(
    (task: DontForgetTaskConfigItem) => {
      navigate(task.route)
    },
    [navigate]
  )

  const handleActivityClick = useCallback(() => {
    navigate(GamerRoutes.UPCOMING_ACTIVITIES)
  }, [navigate])

  // Derive sprintId from the first upcoming activity
  const sprintId = useMemo(() => {
    return upcomingActivities.find((a) => a.sprintId)?.sprintId ?? null
  }, [upcomingActivities])

  return (
    <PageWrapper
      className={className}
      sx={{
        minHeight: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto'
      }}
    >
      {/* Header */}
      <PageHeader
        badgeText={t('gamer.dashboard.badge')}
        badgeIcon={<HomeIcon />}
        title={t('gamer.dashboard.title', { name: userName })}
        subtitle={t('gamer.dashboard.subtitle')}
      />

      {/* Sprint Progress - main overview component */}
      {sprintId && (
        <Box sx={{ mb: 3 }}>
          <SprintGamerProgress
            sprintId={sprintId}
            userTeamIds={userData?.teamIds || []}
            onActivityClick={handleActivityClick}
          />
        </Box>
      )}

      {/* Bottom Row - Try This Next Time and Don't Forget */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr', lg: '1fr 1fr' },
          gap: 3,
          mb: 3
        }}
      >
        {/* Try This Next Time */}
        <TryThisNextTimeComponent
          tryThisNextTime={latestFeedbackData?.tryThisNextTime}
          className="dashboard-try-this-next-time"
        />
        <DontForgetTaskComponent
          className="dashboard-dont-forget"
          user={userData}
          configOptions={{ showFeedbackTask }}
          onTaskNavigate={handleDontForgetNavigation}
        />
      </Box>

      {/* Try This At Work - Full Width */}
      <Box sx={{ mb: 3 }}>
        <TryThisAtWorkComponent
          tryThisAtWork={latestFeedbackData?.tryThisAtWork}
          className="dashboard-try-this-at-work"
        />
      </Box>
    </PageWrapper>
  )
}
